import type { GraphQLResult } from 'aws-amplify/api'
import { useMemo } from 'react'
import type { SearchProductInput } from '@skop-app/lib-search'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import type { Place } from '@skop-app/lib-domain/types'

import {
  getPlaceByIdQuery,
  placesByMasterOrganizationQuery,
  placesByOrganizationQuery,
  placesByUserQuery,
  searchProductTotalQuery,
} from '@/services/graphql'
import { getAmplifyClient } from '@/services/amplifyClient'
import { useUserContext } from '@/context'
import { referenceQueryPreset } from '@/services/query-presets'
import {
  buildGroupedPlaceOptions,
  buildPlaceDirectoryFilter,
  buildPlacePickerOption,
  type PlaceDirectoryFilters,
  type PlaceOptionGroup,
  type PlacePickerOption,
  sortPlacePickerOptions,
} from '@/services/place/place.utils'

type PlaceFilter = Record<string, unknown>

type PlaceQueryScope =
  | { kind: 'master'; value: string }
  | { kind: 'organization'; value: string }
  | { kind: 'user'; value: string }

export type PlaceDirectoryState = {
  places: Place[]
}

export type PlacePickerState = {
  groupedPlacesOptions: PlaceOptionGroup[]
  places: Place[]
  placesOptions: PlacePickerOption[]
}

const client = getAmplifyClient()

const buildPlaceFilter = (
  baseFilter: PlaceFilter | undefined,
  extraFilter: PlaceFilter | undefined,
): PlaceFilter | undefined => {
  if (baseFilter && extraFilter) {
    return {
      and: [baseFilter, extraFilter],
    }
  }

  return baseFilter ?? extraFilter
}

export const getPlaceQueryScopes = (params: {
  organizationID?: string
  organizationMasterID?: string
  userID?: string
}): PlaceQueryScope[] => {
  const { organizationID, organizationMasterID, userID } = params

  if (organizationID) {
    return [
      { kind: 'organization', value: organizationID },
      ...(organizationMasterID && organizationMasterID !== organizationID
        ? [{ kind: 'master', value: organizationMasterID } as const]
        : []),
    ]
  }

  return userID ? [{ kind: 'user', value: userID }] : []
}

const listPlacesPageForScope = async (params: {
  filter?: PlaceFilter
  limit: number
  nextToken?: string | null
  scope: PlaceQueryScope
}): Promise<{ items: Place[]; nextToken: string | null }> => {
  const { filter, limit, nextToken, scope } = params
  const queryConfig =
    scope.kind === 'organization'
      ? {
          query: placesByOrganizationQuery,
          field: 'placesByOrganization',
          variables: {
            organizationID: scope.value,
            sortDirection: 'ASC',
            filter,
            limit,
            nextToken,
          },
        }
      : scope.kind === 'master'
        ? {
            query: placesByMasterOrganizationQuery,
            field: 'placesByMasterOrganization',
            variables: {
              organizationMasterID: scope.value,
              sortDirection: 'ASC',
              filter,
              limit,
              nextToken,
            },
          }
        : {
            query: placesByUserQuery,
            field: 'placesByUser',
            variables: {
              userID: scope.value,
              sortDirection: 'ASC',
              filter: buildPlaceFilter(filter, {
                organizationID: { attributeExists: false },
              }),
              limit,
              nextToken,
            },
          }

  try {
    const { data } = (await client.graphql({
      query: queryConfig.query,
      variables: queryConfig.variables,
    })) as GraphQLResult<
      Record<string, { items: Place[]; nextToken?: string | null }>
    >
    const result = data?.[queryConfig.field]

    return {
      items: result?.items ?? [],
      nextToken: result?.nextToken ?? null,
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

const dedupePlaces = (places: readonly Place[]): Place[] => [
  ...new Map(places.map((place) => [place.id, place] as const)).values(),
]

const sortPlacesByName = (places: readonly Place[]): Place[] =>
  [...places].sort((left, right) =>
    (left.name ?? '').localeCompare(right.name ?? ''),
  )

export const listAllPlaces = async (params: {
  filter?: PlaceFilter
  limit?: number
  scopes: PlaceQueryScope[]
}): Promise<Place[]> => {
  const { filter, limit = 1000, scopes } = params

  const results = await Promise.all(
    scopes.map(async (scope) => {
      const items: Place[] = []
      let nextToken: string | null = null

      do {
        const page = await listPlacesPageForScope({
          scope,
          filter,
          limit,
          nextToken,
        })
        items.push(...page.items)
        nextToken = page.nextToken
      } while (nextToken)

      return items
    }),
  )

  return dedupePlaces(results.flat())
}

export const countPlaceInventoryUsage = async (params: {
  organizationID?: string
  placeID: string
  userID?: string
}): Promise<number> => {
  const { organizationID, placeID, userID } = params

  if (!placeID || (!organizationID && !userID)) return 0

  const filter: SearchProductInput = {
    placeID: { eq: placeID },
    nature: { ne: 'service' },
    ...(organizationID
      ? { organizationID: { eq: organizationID } }
      : {
          byUserID: { eq: userID },
          organizationID: { exists: false },
        }),
  }

  try {
    const { data } = (await client.graphql({
      query: searchProductTotalQuery,
      variables: { filter },
    })) as GraphQLResult<{
      searchProductStatusV2: {
        total?: number | null
      }
    }>

    return data?.searchProductStatusV2?.total ?? 0
  } catch (error) {
    console.error('error counting place inventory usage', error)
    throw error
  }
}

export const usePlacePickerOptions = (): UseQueryResult<PlacePickerState> => {
  const { ownership, organizationScope } = useUserContext()
  const scopes = useMemo(
    () =>
      getPlaceQueryScopes({
        userID: ownership?.byUserID,
        organizationID: organizationScope.activeOrganizationId,
        organizationMasterID: organizationScope.activeOrganizationMasterId,
      }),
    [
      ownership?.byUserID,
      organizationScope.activeOrganizationId,
      organizationScope.activeOrganizationMasterId,
    ],
  )

  return useQuery({
    queryKey: ['places', 'picker', JSON.stringify(scopes)],
    queryFn: async () => {
      const places = sortPlacesByName(await listAllPlaces({ scopes }))
      const placesOptions = sortPlacePickerOptions(
        places.map(buildPlacePickerOption),
      )

      return {
        places,
        placesOptions,
        groupedPlacesOptions: buildGroupedPlaceOptions(placesOptions),
      }
    },
    ...referenceQueryPreset,
    enabled: scopes.length > 0,
    placeholderData: {
      places: [],
      placesOptions: [],
      groupedPlacesOptions: [],
    },
  })
}

export const usePlacesDirectory = ({
  filters,
}: {
  filters: PlaceDirectoryFilters
}): UseQueryResult<PlaceDirectoryState> => {
  const { ownership, organizationScope } = useUserContext()
  const scopes = useMemo(
    () =>
      getPlaceQueryScopes({
        userID: ownership?.byUserID,
        organizationID: organizationScope.activeOrganizationId,
        organizationMasterID: organizationScope.activeOrganizationMasterId,
      }),
    [
      ownership?.byUserID,
      organizationScope.activeOrganizationId,
      organizationScope.activeOrganizationMasterId,
    ],
  )
  const directoryFilter = useMemo(
    () => buildPlaceDirectoryFilter(undefined, filters),
    [filters],
  )

  return useQuery({
    queryKey: [
      'places',
      'directory',
      JSON.stringify(scopes),
      JSON.stringify(directoryFilter),
    ],
    queryFn: async () => {
      return {
        places: sortPlacesByName(
          await listAllPlaces({
            scopes,
            filter: directoryFilter,
          }),
        ),
      }
    },
    ...referenceQueryPreset,
    enabled: scopes.length > 0,
    placeholderData: (previous) =>
      previous ?? {
        places: [],
      },
  })
}

export const usePlaces = (): UseQueryResult<PlacePickerState> =>
  usePlacePickerOptions()

export const getPlaceById = async (id: string): Promise<Place> => {
  const {
    data: { getPlace },
  } = (await client.graphql({
    query: getPlaceByIdQuery,
    variables: { id },
  })) as GraphQLResult<{ getPlace: Place }>

  return getPlace
}
